import React, { useMemo } from 'react'
import { Flame } from 'lucide-react'
import { Heading } from '@astryxdesign/core/Heading'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Text } from '@astryxdesign/core/Text'
import { HoverCard } from '@astryxdesign/core/HoverCard'
import {
  buildWeek,
  currentStreak,
  dayLabel,
  formatLocalDate,
  longestStreak,
  STREAK_THRESHOLD_WORDS,
  weekRangeLabel,
  type WritingDay
} from '../lib/writingDays'

const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

interface StreakCalendarProps {
  days: WritingDay[]
}

function dotClassName(params: {
  hasMetThreshold: boolean
  words: number
  isFuture: boolean
}): string {
  const classes = ['streak-dot']
  if (params.hasMetThreshold) classes.push('streak-dot-met')
  else if (params.isFuture) classes.push('streak-dot-future')
  else if (params.words > 0) classes.push('streak-dot-partial')
  return classes.join(' ')
}

export default function StreakCalendar({ days }: StreakCalendarProps): React.JSX.Element {
  const today = formatLocalDate(new Date())
  const week = useMemo(() => buildWeek(today, days), [today, days])
  const streak = useMemo(() => currentStreak(days, today), [days, today])
  const best = useMemo(() => longestStreak(days), [days])
  const range = weekRangeLabel(today)

  return (
    <section className="home-side-card">
      <HStack justify="between" align="center">
        <Heading level={3}>Writing streak</Heading>
        <HStack gap={1} align="center" className="streak-count">
          <Flame size={16} aria-hidden="true" />
          <Text type="label">
            {streak} {streak === 1 ? 'day' : 'days'}
          </Text>
        </HStack>
      </HStack>

      <VStack gap={2}>
        <section className="streak-week" aria-hidden="true">
          {WEEKDAY_INITIALS.map((initial, index) => (
            <span key={`${initial}-${index}`} className="streak-weekday">
              {initial}
            </span>
          ))}
        </section>
        <section className="streak-week" role="list" aria-label={`Writing days, ${range}`}>
          {week.map((cell, index) => {
            const isFuture = cell.day > today
            // The connector is drawn by the earlier day of each qualifying pair and reaches
            // into the next slot, so a run of gold days renders as one continuous band
            // without two half-bars overlapping (which would double the 50% alpha).
            const linksToNext = cell.hasMetThreshold && week[index + 1]?.hasMetThreshold === true
            const dayOfMonth = Number(cell.day.slice(8))
            const date = dayLabel(cell.day)
            const count = isFuture
              ? 'Not yet'
              : `${cell.words.toLocaleString()} ${cell.words === 1 ? 'word' : 'words'}`

            return (
              <span
                key={cell.day}
                role="listitem"
                aria-label={`${date}, ${count}${cell.hasMetThreshold ? ', goal met' : ''}`}
                className={`streak-slot${linksToNext ? ' is-linked' : ''}`}
              >
                <HoverCard
                  // Default 'auto' hover indication draws a dashed underline, which reads as a
                  // defect on a row of dots.
                  hasHoverIndication={false}
                  content={
                    <VStack gap={1} className="streak-hovercard">
                      <Text type="label">{date}</Text>
                      <Text type="supporting">{count}</Text>
                    </VStack>
                  }
                >
                  <span
                    aria-hidden="true"
                    className={dotClassName({
                      hasMetThreshold: cell.hasMetThreshold,
                      words: cell.words,
                      isFuture
                    })}
                  >
                    {dayOfMonth}
                  </span>
                </HoverCard>
              </span>
            )
          })}
        </section>
      </VStack>

      <Text type="supporting" className="streak-message">
        {streak > 0
          ? `Write ${STREAK_THRESHOLD_WORDS.toLocaleString()} words today to continue your streak!`
          : `Write ${STREAK_THRESHOLD_WORDS.toLocaleString()} words today to start a streak!`}
      </Text>

      {/* A best of 0 or 1 is not a streak worth naming — it just restates today. */}
      {best >= 2 && (
        <HStack justify="between" align="center">
          <Text type="supporting">Longest streak</Text>
          <Text type="label">{best} days</Text>
        </HStack>
      )}
    </section>
  )
}
